class ThirdPartyTesting extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.products = this.readProducts();

    this.productSelect = this.querySelector('[data-tpt-product-select]');
    this.variantSelect = this.querySelector('[data-tpt-variant-select]');
    this.lotField = this.querySelector('[data-tpt-lot-field]');
    this.lotSelect = this.querySelector('[data-tpt-lot-select]');
    this.selectedTitleElement = this.querySelector('[data-tpt-title]');
    this.descriptionElement = this.querySelector('[data-tpt-description]');
    this.link = this.querySelector('[data-tpt-link]');
    this.unavailable = this.querySelector('[data-tpt-unavailable]');
    this.image = this.querySelector('[data-tpt-image]');
    this.imagePlaceholder = this.querySelector('[data-tpt-image-placeholder]');

    if (!this.productSelect || !this.variantSelect || this.products.length === 0) return;

    this.productSelect.addEventListener('change', () => this.handleProductChange());
    this.variantSelect.addEventListener('change', () => this.handleVariantChange());
    this.lotSelect?.addEventListener('change', () => this.updateResult());

    if (!this.productSelect.value) {
      this.productSelect.value = '0';
    }

    this.handleProductChange();
  }

  readProducts() {
    const script = this.querySelector('[data-third-party-testing-products]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Third party testing data could not be parsed.', error);
      return [];
    }
  }

  handleProductChange() {
    const product = this.selectedProduct;

    this.variantSelect.replaceChildren();

    if (!product?.variants?.length) {
      this.variantSelect.disabled = true;
      this.variantSelect.appendChild(this.createOption('', this.dataset.emptyVariantLabel || 'No variants available'));
      this.populateLots(null);
      this.updateResult();
      return;
    }

    product.variants.forEach((variant, index) => {
      const label = variant.title === 'Default Title' ? this.dataset.defaultVariantLabel || 'Standard' : variant.title;
      const option = this.createOption(String(index), label);

      if (this.getLotOptions(variant).some((lot) => lot.coaUrl)) {
        option.dataset.hasCoa = 'true';
      }

      this.variantSelect.appendChild(option);
    });

    this.variantSelect.disabled = false;
    this.variantSelect.value = '0';
    this.handleVariantChange();
  }

  handleVariantChange() {
    this.populateLots(this.selectedVariant);
    this.updateResult();
  }

  populateLots(variant) {
    if (!this.lotSelect || !this.lotField) return;

    const lots = this.getLotOptions(variant);

    this.lotSelect.replaceChildren();

    if (!lots.length) {
      this.lotSelect.disabled = true;
      this.lotField.hidden = true;
      this.lotSelect.appendChild(this.createOption('', this.dataset.emptyLotLabel || 'No lot codes available'));
      return;
    }

    lots.forEach((lot, index) => {
      const option = this.createOption(String(index), lot.label || lot.lotCode || this.dataset.defaultLotLabel || 'Current lot');

      if (lot.coaUrl) {
        option.dataset.hasCoa = 'true';
      }

      this.lotSelect.appendChild(option);
    });

    this.lotSelect.disabled = false;
    this.lotSelect.value = '0';
    this.lotField.hidden = lots.length <= 1;
  }

  createOption(value, label) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    return option;
  }

  updateResult() {
    const product = this.selectedProduct;
    const variant = this.selectedVariant;
    const lot = this.selectedLot;

    if (!product || !variant) {
      this.setText(this.selectedTitleElement, this.dataset.emptyTitle || 'Select a product');
      this.setText(this.descriptionElement, this.dataset.emptyText || '');
      this.hideLink();
      this.showUnavailable(false);
      this.updateImage(null);
      return;
    }

    const selectedTitle = variant.title === 'Default Title' ? product.title : variant.title;

    this.setText(this.selectedTitleElement, selectedTitle);

    if (lot?.coaUrl) {
      this.setText(
        this.descriptionElement,
        this.dataset.readyText || 'The certificate of analysis for this selection is ready to view.'
      );
      this.showLink(lot.coaUrl);
      this.showUnavailable(false);
    } else {
      this.setText(
        this.descriptionElement,
        this.dataset.missingText || 'No certificate of analysis is attached to this variant yet.'
      );
      this.hideLink();
      this.showUnavailable(true);
    }

    this.updateImage(variant.image || product.image);
  }

  getLotOptions(variant) {
    if (!variant) return [];

    if (Array.isArray(variant.lots) && variant.lots.length > 0) {
      return variant.lots;
    }

    if (variant.coaUrl) {
      return [
        {
          lotCode: '',
          label: this.dataset.defaultLotLabel || 'Current lot',
          coaUrl: variant.coaUrl,
        },
      ];
    }

    return [];
  }

  showLink(url) {
    if (!this.link) return;

    this.link.href = url;
    this.link.hidden = false;

    const labelTarget = this.link.querySelector('[data-tpt-link-label]');
    if (labelTarget) {
      labelTarget.textContent = this.dataset.buttonLabel || labelTarget.textContent;
    }
  }

  hideLink() {
    if (!this.link) return;

    this.link.removeAttribute('href');
    this.link.hidden = true;
  }

  showUnavailable(show) {
    if (!this.unavailable) return;

    this.unavailable.hidden = !show;
  }

  updateImage(url) {
    if (!this.image || !this.imagePlaceholder) return;

    if (!url) {
      this.image.hidden = true;
      this.image.removeAttribute('src');
      this.imagePlaceholder.hidden = false;
      return;
    }

    this.image.src = url;
    this.image.hidden = false;
    this.imagePlaceholder.hidden = true;
  }

  setText(element, text) {
    if (element) {
      element.textContent = text;
    }
  }

  get selectedProduct() {
    const index = Number(this.productSelect?.value || 0);
    return this.products[index] || null;
  }

  get selectedVariant() {
    const product = this.selectedProduct;
    const index = Number(this.variantSelect?.value || 0);
    return product?.variants?.[index] || null;
  }

  get selectedLot() {
    const lots = this.getLotOptions(this.selectedVariant);
    const index = Number(this.lotSelect?.value || 0);
    return lots[index] || null;
  }
}

if (!customElements.get('third-party-testing')) {
  customElements.define('third-party-testing', ThirdPartyTesting);
}
